import React, { useEffect, useRef } from "react";

/**
 * RE₿EL Simulator — Pure Canvas candle chart (no external lib).
 * Renders the last `visibleCount` candles with volume panel below.
 * Highlights latest price with blinking cyan line + tags liquidity zones.
 */
export const CandleChart = ({ candles, visibleCount = 60, liquidityZones = [] }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const cvs = canvasRef.current;
    const ct = containerRef.current;
    if (!cvs || !ct) return;
    const dpr = window.devicePixelRatio || 1;

    const draw = () => {
      const w = ct.clientWidth;
      const h = ct.clientHeight;
      cvs.width = w * dpr;
      cvs.height = h * dpr;
      cvs.style.width = `${w}px`;
      cvs.style.height = `${h}px`;
      const ctx = cvs.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, w, h);

      // Panels: top 78% price, bottom 22% volume
      const priceH = h * 0.78;
      const volH = h * 0.22;
      const volY = priceH;

      const visible = candles.slice(-visibleCount);
      if (visible.length === 0) return;

      const highs = visible.map((c) => c.high);
      const lows = visible.map((c) => c.low);
      const vols = visible.map((c) => c.volume);
      const max = Math.max(...highs);
      const min = Math.min(...lows);
      const pad = (max - min) * 0.05 || max * 0.01;
      const yMax = max + pad;
      const yMin = min - pad;
      const range = yMax - yMin || 1;
      const candleSlot = w / visibleCount;
      const candleW = Math.max(2, candleSlot * 0.68);

      const yPrice = (p) => priceH - ((p - yMin) / range) * priceH;

      // ---- background grid ----
      ctx.strokeStyle = "rgba(28,36,69,0.5)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 1; i < 5; i++) {
        const y = (priceH / 5) * i;
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
      }
      ctx.stroke();

      // Price scale labels (right side)
      ctx.fillStyle = "rgba(180,200,230,0.32)";
      ctx.font = "10px ui-monospace, monospace";
      ctx.textAlign = "right";
      for (let i = 0; i <= 5; i++) {
        const p = yMin + (range * (5 - i)) / 5;
        const y = (priceH / 5) * i;
        ctx.fillText(`$${p.toFixed(p < 100 ? 2 : 0)}`, w - 6, Math.max(10, y + 3));
      }

      // ---- liquidity zones (drawn behind candles) ----
      if (Array.isArray(liquidityZones)) {
        liquidityZones.forEach((z) => {
          if (z.price < yMin || z.price > yMax) return;
          const y = yPrice(z.price);
          ctx.save();
          ctx.fillStyle = z.side === "buy" ? "rgba(0,212,255,0.10)" : "rgba(255,51,85,0.10)";
          ctx.fillRect(0, y - 6, w, 12);
          ctx.strokeStyle = z.side === "buy" ? "rgba(0,212,255,0.45)" : "rgba(255,51,85,0.45)";
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(w, y);
          ctx.stroke();
          ctx.restore();
        });
      }

      // ---- candles ----
      visible.forEach((c, i) => {
        const cx = i * candleSlot + candleSlot / 2;
        const up = c.close >= c.open;
        const color = up ? "#00e676" : "#ff3355";
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = 1.2;
        // wick
        ctx.beginPath();
        ctx.moveTo(cx, yPrice(c.high));
        ctx.lineTo(cx, yPrice(c.low));
        ctx.stroke();
        // body
        const yo = yPrice(c.open);
        const yc = yPrice(c.close);
        const top = Math.min(yo, yc);
        const bh = Math.max(1, Math.abs(yc - yo));
        ctx.fillRect(cx - candleW / 2, top, candleW, bh);
      });

      // ---- current price marker ----
      const last = visible[visible.length - 1];
      if (last) {
        const y = yPrice(last.close);
        const up = last.close >= last.open;
        const blinkA = 0.55 + 0.4 * Math.sin(Date.now() / 360);
        ctx.save();
        ctx.strokeStyle = up
          ? `rgba(0,230,118,${blinkA})`
          : `rgba(255,51,85,${blinkA})`;
        ctx.setLineDash([3, 5]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
        ctx.restore();
        // price label box
        const label = `$${last.close.toFixed(last.close < 100 ? 2 : 0)}`;
        ctx.fillStyle = up ? "#00e676" : "#ff3355";
        const labelW = Math.max(60, ctx.measureText(label).width + 16);
        ctx.fillRect(w - labelW - 2, y - 9, labelW, 18);
        ctx.fillStyle = "#000";
        ctx.font = "bold 11px ui-monospace, monospace";
        ctx.textAlign = "right";
        ctx.fillText(label, w - 10, y + 4);
      }

      // ---- volume panel ----
      const vMax = Math.max(...vols) || 1;
      ctx.fillStyle = "rgba(20,28,52,0.6)";
      ctx.fillRect(0, volY, w, volH);
      ctx.strokeStyle = "rgba(28,36,69,0.5)";
      ctx.beginPath();
      ctx.moveTo(0, volY);
      ctx.lineTo(w, volY);
      ctx.stroke();
      visible.forEach((c, i) => {
        const cx = i * candleSlot + candleSlot / 2;
        const up = c.close >= c.open;
        ctx.fillStyle = up ? "rgba(0,230,118,0.55)" : "rgba(255,51,85,0.55)";
        const bh = (c.volume / vMax) * (volH - 4);
        ctx.fillRect(cx - candleW / 2, volY + volH - bh - 2, candleW, bh);
      });

      // VOL label
      ctx.fillStyle = "rgba(180,200,230,0.45)";
      ctx.font = "9px ui-monospace, monospace";
      ctx.textAlign = "left";
      ctx.fillText("VOL", 6, volY + 12);
    };

    draw();
    let raf;
    const tick = () => {
      draw();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const ro = new ResizeObserver(draw);
    ro.observe(ct);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [candles, visibleCount, liquidityZones]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden rounded-2xl border border-white/10 bg-[#05060d]"
      data-testid="sim-candle-chart"
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
};

export default CandleChart;
